import { LOCIZE_NS, MUTATION_KEYS } from '@avanta/shared/constants/common';
import { AppointmentService } from '@avanta/shared/services/appointments';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { type AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { QUERY_KEYS } from 'constants/common';
import { ROUTES } from 'constants/routes';
import { type BookAppointmentVariables } from 'pages/AppointmentsReserve/types';
import { type Appointment } from 'types/appointments';
import { useUploadDocument } from './useUploadDocument';

export const useBookAppointment = (): UseMutationResult<
  AxiosResponse<Appointment>,
  Error,
  BookAppointmentVariables
> => {
  const navigate = useNavigate();
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const { mutate: uploadDocument } = useUploadDocument();
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<Appointment>, Error, BookAppointmentVariables>({
    mutationKey: [MUTATION_KEYS.BOOK_APPOINTMENT],
    mutationFn: ({ payload }) => AppointmentService.book(payload),
    onSuccess: async ({ data }, { document, patient, psychologist, center }) => {
      if (document && data) {
        uploadDocument({ appointmentId: data.id, document });
      }
      toast.success(t('AppointmentsReserve.success'), { id: 'book-appointment-success' });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FETCH_ENTERPRISE_APPOINTMENTS] });
      navigate(ROUTES.APPOINTMENTS_SUCCESS, { state: { ...data, patient, psychologist, center } });
    },
    onError: () => {
      toast.error(t('AppointmentsReserve.error'), { id: 'book-appointment-error' });
    },
  });
};
